/**
 * Signing in against Microsoft Entra ID.
 *
 * The application's own half of a person still comes from `/auth/me` — this only obtains the token
 * that call is made with. Entra says who somebody is; the API says what they may do, and the two
 * are deliberately not the same question. A permission has never been read from a token payload
 * here and still is not.
 *
 * Redirect rather than popup. A popup is pleasanter until a browser blocks it, and it fails in
 * exactly the situation nobody can reproduce — a staff laptop with a strict pop-up policy. Redirect
 * has one awkward moment, the return to the page, and that moment is handled once in
 * `completeSignIn` below.
 *
 * Auth state lives in **session** storage, not memory. That is not the choice I would make on
 * security grounds alone — `api/client.ts` keeps the access token in memory precisely so a script
 * on the origin cannot read it — but a redirect flow navigates the page away and back, and an
 * in-memory cache does not survive that. MSAL refuses the combination outright with
 * `in_mem_redirect_unavailable`, which is what the first real sign-in attempt hit (2026-09-15).
 *
 * The alternatives were worse. A popup keeps memory-only state but fails on exactly the managed
 * laptops this is for. `localStorage` persists across tabs and restarts, which is strictly more
 * exposure than needed. Session storage is scoped to the tab and cleared when it closes, which is
 * the least that makes redirect work.
 *
 * This only holds MSAL's own auth state and its token cache. The application's access token is
 * still handed to `api/client.ts`, which keeps it in memory as before.
 */
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';

/** The scope the API exposes. Requested by name so the token is for this API, not for Graph. */
const API_SCOPE = import.meta.env.VITE_ENTRA_API_SCOPE ?? '';

const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '';
const AUTHORITY = import.meta.env.VITE_ENTRA_AUTHORITY ?? '';

/**
 * Whether this build has been told enough to sign in against Entra.
 *
 * Checked rather than assumed: a build with `VITE_AUTH_PROVIDER=entra` and no client id would
 * otherwise fail inside MSAL with a message about a malformed request, which is a long way from
 * "somebody forgot a variable".
 */
export function isEntraConfigured(): boolean {
  return CLIENT_ID !== '' && AUTHORITY !== '' && API_SCOPE !== '';
}

export function entraConfigurationProblem(): string | null {
  const missing = [
    CLIENT_ID === '' ? 'VITE_ENTRA_CLIENT_ID' : null,
    AUTHORITY === '' ? 'VITE_ENTRA_AUTHORITY' : null,
    API_SCOPE === '' ? 'VITE_ENTRA_API_SCOPE' : null,
  ].filter((name): name is string => name !== null);
  return missing.length === 0 ? null : `This build is missing ${missing.join(', ')}.`;
}

const configuration: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // Session, not memory — see the note at the top of this file. Redirect flows require storage
    // that survives the navigation; memory does not, and MSAL rejects the pair.
    cacheLocation: 'sessionStorage',
  },
};

let instance: PublicClientApplication | null = null;

/** The MSAL instance, initialised once. */
async function client(): Promise<PublicClientApplication> {
  if (instance === null) {
    instance = new PublicClientApplication(configuration);
    await instance.initialize();
  }
  return instance;
}

function firstAccount(app: PublicClientApplication): AccountInfo | null {
  return app.getAllAccounts()[0] ?? null;
}

/**
 * Finish a redirect that is landing now, and return the token if one arrived.
 *
 * Called once on start-up before anything else asks about the user. `handleRedirectPromise`
 * resolves to null on an ordinary page load, which is the common case and not an error.
 */
export async function completeSignIn(): Promise<string | null> {
  const app = await client();
  const result: AuthenticationResult | null = await app.handleRedirectPromise();
  if (result?.accessToken) {
    app.setActiveAccount(result.account);
    return result.accessToken;
  }
  const account = firstAccount(app);
  if (account === null) return null;
  app.setActiveAccount(account);
  return silentToken();
}

/** Send the browser to Microsoft. Does not return: the page navigates away. */
export async function beginSignIn(): Promise<void> {
  const app = await client();
  await app.loginRedirect({ scopes: [API_SCOPE] });
}

/**
 * A token for the API without prompting, or null.
 *
 * `InteractionRequiredAuthError` is the expected way this fails — a consent that has been revoked,
 * a session that has expired, a conditional-access policy that wants a fresh sign-in — and the
 * honest answer to all of them is to ask the person to sign in again rather than to loop.
 */
export async function silentToken(refresh = false): Promise<string | null> {
  const app = await client();
  const account = app.getActiveAccount() ?? firstAccount(app);
  if (account === null) return null;
  try {
    // Asked on every API call (see `setTokenSource`): MSAL answers from its cache and renews a
    // token close to expiry itself. `refresh` forces a renewal, for a call the API just refused.
    const result = await app.acquireTokenSilent({
      scopes: [API_SCOPE],
      account,
      forceRefresh: refresh,
    });
    return result.accessToken;
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) return null;
    throw error;
  }
}

/** Sign out of Entra as well as the application. */
export async function endSignIn(): Promise<void> {
  const app = await client();
  const account = app.getActiveAccount() ?? firstAccount(app);
  await app.logoutRedirect({
    account: account ?? undefined,
    postLogoutRedirectUri: window.location.origin,
  });
}
